import React from 'react';
import { Button, Popconfirm } from 'antd';
import {
  EditOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
// Api
import { UPDATE_CHANNEL_MONETIZATION_PROGRAM } from 'api/channelMonetization/mutations';
// Types
import { ListChannelMonetizationPrograms_listChannelMonetizationPrograms } from 'api/channelMonetization/types/ListChannelMonetizationPrograms';
import {
  UpdateChannelMonetizationProgram,
  UpdateChannelMonetizationProgramVariables,
} from 'api/channelMonetization/types/UpdateChannelMonetizationProgram';
import { ChannelMonetizationProgramStatus } from 'api/graphql-global-types';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './ProgramActions.module.scss';

type ProgramActionsProps = {
  program: ListChannelMonetizationPrograms_listChannelMonetizationPrograms;
  onEdit: (
    program: ListChannelMonetizationPrograms_listChannelMonetizationPrograms
  ) => void;
  onRefetch: () => void;
};

const ProgramActions: React.FC<ProgramActionsProps> = ({
  program,
  onEdit,
  onRefetch,
}) => {
  const [updateProgram, { loading }] = useMutation<
    UpdateChannelMonetizationProgram,
    UpdateChannelMonetizationProgramVariables
  >(UPDATE_CHANNEL_MONETIZATION_PROGRAM);

  const handleEdit = () => {
    onEdit(program);
  };

  const setStatus = async (status: ChannelMonetizationProgramStatus) => {
    try {
      await updateProgram({
        variables: { input: { programId: program.id, status } },
      });
      successNotification(`Program is now ${status}`);
      onRefetch();
    } catch (error) {
      errorNotification((error as Error).message);
    }
  };

  const isOpen = program.status === ChannelMonetizationProgramStatus.Open;
  const isClosed = program.status === ChannelMonetizationProgramStatus.Closed;
  const openLabel = isClosed ? 'Reopen' : 'Open';

  return (
    <div className={styles.actionsContainer}>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={handleEdit}
        disabled={loading}
      >
        Edit
      </Button>

      {!isOpen && (
        <Popconfirm
          title="Opening this program will close any other Open program. Continue?"
          onConfirm={() => setStatus(ChannelMonetizationProgramStatus.Open)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" icon={<PlayCircleOutlined />} disabled={loading}>
            {openLabel}
          </Button>
        </Popconfirm>
      )}

      {isOpen && (
        <Popconfirm
          title="Closing this program stops event tracking against it. Continue?"
          onConfirm={() => setStatus(ChannelMonetizationProgramStatus.Closed)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<StopOutlined />} disabled={loading}>
            Close
          </Button>
        </Popconfirm>
      )}
    </div>
  );
};

export default ProgramActions;
